/**
 * QC Inspector Dashboard - DSH-FE-002
 * AS9100D-Compliant Quality Control Inspector Command Center
 * 
 * Role Focus:
 * - Pending inspection queue (priority sorted)
 * - QC release/reject decisions
 * - Quarantine disposition workflow
 * - Performance metrics and compliance
 * 
 * AS9100D Clauses:
 * - 8.4.2: External Provider Control - Inspection verification
 * - 8.6: Release of Products - QC release authority
 * - 8.7: Control of Nonconforming Outputs - Quarantine/disposition
 * - 9.1: Monitoring and Measurement - Performance tracking
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  TrendingUp,
  Package,
  FileCheck,
  AlertOctagon,
  ChevronRight,
  RefreshCw,
  Loader2,
  Eye,
  CheckCircle,
  Ban,
  Activity,
  Wifi,
  WifiOff,
  ArrowRight,
  FileWarning,
  Timer,
  BarChart3,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import { useInventoryWebSocket } from '../hooks/useInventoryWebSocket';
import { DashboardSelector } from '../hooks/useInventoryDashboard';
import { PartChangeNotificationPanel } from '../components/PartChangeNotificationPanel';
import InventoryLayout from '../components/layouts/InventoryLayout';

/**
 * Summary Card for QC Inspector metrics
 */
const QCSummaryCard = ({ 
  title, 
  count, 
  subtitle, 
  icon: Icon, 
  color = 'blue',
  urgent = false,
  badge = null,
  onClick,
  testId
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'bg-blue-100 text-blue-600',
      border: urgent ? 'border-blue-400' : 'border-blue-100',
      badge: 'bg-blue-100 text-blue-700'
    },
    green: {
      bg: 'bg-green-50',
      icon: 'bg-green-100 text-green-600',
      border: 'border-green-100',
      badge: 'bg-green-100 text-green-700'
    },
    amber: {
      bg: 'bg-amber-50',
      icon: 'bg-amber-100 text-amber-600',
      border: urgent ? 'border-amber-400' : 'border-amber-100',
      badge: 'bg-amber-100 text-amber-700'
    },
    red: {
      bg: 'bg-red-50',
      icon: 'bg-red-100 text-red-600',
      border: urgent ? 'border-red-400 border-2' : 'border-red-100',
      badge: 'bg-red-100 text-red-700'
    },
    purple: {
      bg: 'bg-purple-50',
      icon: 'bg-purple-100 text-purple-600',
      border: 'border-purple-100',
      badge: 'bg-purple-100 text-purple-700'
    },
    slate: {
      bg: 'bg-slate-50',
      icon: 'bg-slate-100 text-slate-600',
      border: 'border-slate-100',
      badge: 'bg-slate-100 text-slate-700'
    },
    emerald: {
      bg: 'bg-emerald-50',
      icon: 'bg-emerald-100 text-emerald-600',
      border: 'border-emerald-100',
      badge: 'bg-emerald-100 text-emerald-700'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <Card 
      className={`p-4 ${colors.bg} border ${colors.border} cursor-pointer hover:shadow-md transition-all group`}
      onClick={onClick}
      data-testid={testId || `qc-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colors.icon}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-gray-600">{title}</span>
            {badge && (
              <Badge variant="outline" className={`text-xs ${colors.badge}`}>
                {badge}
              </Badge>
            )}
          </div>
          <p className="text-3xl font-bold text-[#101957] mt-2">
            {typeof count === 'number' && count % 1 !== 0 ? `${count}%` : count}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
    </Card>
  );
};

/**
 * Inspection Queue Item Component
 */
const InspectionQueueItem = ({ inspection, onInspect }) => {
  const priorityConfig = {
    CRITICAL: { color: 'bg-red-100 text-red-700 border-red-200', label: '🔴 CRITICAL' },
    HIGH: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: '🟠 HIGH' },
    MEDIUM: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: '🟡 MEDIUM' },
    NORMAL: { color: 'bg-slate-100 text-slate-700 border-slate-200', label: '⚪ NORMAL' },
    LOW: { color: 'bg-green-100 text-green-700 border-green-200', label: '🟢 LOW' }
  };

  const config = priorityConfig[inspection.priority] || priorityConfig.NORMAL;
  
  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        inspection.is_overdue ? 'bg-red-50 border-red-200' : 
        inspection.is_counterfeit_suspect ? 'bg-amber-50 border-amber-300' :
        inspection.is_safety_critical ? 'bg-purple-50 border-purple-200' :
        'bg-white border-gray-100'
      } hover:shadow-sm transition-shadow cursor-pointer group`}
      onClick={() => onInspect(inspection)}
      data-testid={`inspection-item-${inspection.inspection_number}`}
    >
      {/* Priority & Status Indicators */}
      <div className="flex flex-col items-center gap-1">
        <div className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
          {inspection.priority || 'NORMAL'}
        </div>
        {inspection.is_counterfeit_suspect && (
          <Badge variant="destructive" className="text-[10px]">⚠️ SUSPECT</Badge>
        )}
        {inspection.is_safety_critical && (
          <Badge className="bg-purple-600 text-[10px]">SAFETY</Badge>
        )}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">
            {inspection.inspection_number}
          </p>
          {inspection.coc_required && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <FileCheck className={`w-4 h-4 ${inspection.coc_attached ? 'text-green-600' : 'text-amber-500'}`} />
                </TooltipTrigger>
                <TooltipContent>
                  {inspection.coc_attached ? 'CoC Attached' : 'CoC Required - Not Yet Attached'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <p className="text-sm text-gray-600 truncate">
          {inspection.part_number || 'N/A'} • Lot: {inspection.lot_number}
        </p>
        <p className="text-xs text-gray-400 truncate">
          GRN: {inspection.grn_number} • Supplier: {inspection.supplier_name || 'Unknown'}
        </p>
      </div>
      
      {/* Quantity & Age */}
      <div className="text-right">
        <p className="font-semibold text-[#101957]">
          {inspection.quantity_to_inspect} {inspection.uom}
        </p>
        <p className={`text-xs ${inspection.is_overdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
          {inspection.is_overdue ? 'OVERDUE' : `${inspection.days_in_queue}d in queue`}
        </p>
      </div>
      
      {/* Action */}
      <Button 
        size="sm" 
        variant="outline"
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => { e.stopPropagation(); onInspect(inspection); }}
      >
        <Eye className="w-4 h-4 mr-1" />
        Inspect
      </Button>
    </div>
  );
};

/**
 * Quarantine Item Component
 */
const QuarantineItem = ({ item, onDisposition }) => {
  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        item.is_counterfeit_suspect ? 'bg-red-50 border-red-300' : 
        item.regulatory_hold ? 'bg-amber-50 border-amber-300' :
        'bg-white border-gray-100'
      } hover:shadow-sm transition-shadow cursor-pointer`}
      onClick={() => onDisposition(item)}
      data-testid={`quarantine-item-${item.lot_number}`}
    >
      {/* Alert Indicator */}
      <div className={`p-2 rounded-lg ${
        item.is_counterfeit_suspect ? 'bg-red-100' : 
        item.regulatory_hold ? 'bg-amber-100' : 
        'bg-slate-100'
      }`}>
        {item.is_counterfeit_suspect ? (
          <AlertOctagon className="w-5 h-5 text-red-600" />
        ) : item.regulatory_hold ? (
          <FileWarning className="w-5 h-5 text-amber-600" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-slate-600" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{item.lot_number}</p>
          {item.is_counterfeit_suspect && (
            <Badge variant="destructive" className="text-xs">COUNTERFEIT SUSPECT</Badge>
          )}
          {item.regulatory_hold && (
            <Badge className="bg-amber-500 text-xs">REGULATORY HOLD</Badge>
          )}
        </div>
        <p className="text-sm text-gray-600 truncate">
          {item.part_number} • Qty: {item.quantity}
        </p>
        <p className="text-xs text-gray-400">
          Reason: {item.quarantine_reason?.replace(/_/g, ' ')}
        </p>
      </div>
      
      {/* Status & Age */}
      <div className="text-right">
        <Badge variant="outline" className="text-xs mb-1">
          {item.status?.replace(/_/g, ' ')}
        </Badge>
        <p className="text-xs text-gray-400">
          {item.days_in_quarantine}d in quarantine
        </p>
      </div>
    </div>
  );
};

/**
 * Recent Activity Item
 */
const RecentActivityItem = ({ activity }) => {
  const resultConfig = {
    'PASS': { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100' },
    'FAIL': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
    'PARTIAL': { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100' }
  };
  
  const config = resultConfig[activity.result] || resultConfig['PASS'];
  const Icon = config.icon;
  
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <div className={`mt-0.5 p-1.5 rounded-full ${config.bg}`}>
        <Icon className={`w-3.5 h-3.5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700">
          <span className="font-medium">{activity.inspection_number}</span> - {activity.result}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {activity.part_number} • Lot: {activity.lot_number}
        </p>
        <p className="text-xs text-gray-400">
          {activity.quantity_accepted > 0 && `✓ ${activity.quantity_accepted} accepted`}
          {activity.quantity_rejected > 0 && ` ✕ ${activity.quantity_rejected} rejected`}
        </p>
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-400">
          {activity.completed_at ? formatIST(activity.completed_at, { includeDate: false, includeSeconds: false }) : ''}
        </p>
      </div>
    </div>
  );
};

/**
 * Performance Metric Card
 */
const PerformanceMetric = ({ label, value, subtext, trend, color = 'blue' }) => (
  <div className="text-center p-3 rounded-lg bg-slate-50">
    <p className="text-2xl font-bold text-[#101957]">{value}</p>
    <p className="text-xs text-gray-600">{label}</p>
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

/**
 * Main QC Inspector Dashboard Component
 */
const QCInspectorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Handle dashboard switch
  const handleDashboardSwitch = (path) => {
    navigate(path);
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Load dashboard data
  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const response = await api.get('/api/v1/inventory/v2/dashboard/qc-inspector');
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading QC inspector dashboard:', err);
      setError('Failed to load dashboard data');
      // Set empty state
      setDashboardData({
        summary_cards: {
          pending_inspections: { count: 0, overdue: 0, critical: 0, subtitle: 'No data' },
          qc_hold_items: { count: 0, urgent: 0, subtitle: 'No data' },
          quarantine_pending: { count: 0, counterfeit_suspect: 0, subtitle: 'No data' },
          completed_today: { count: 0, week_total: 0, subtitle: '' },
          pass_rate: { count: 0, pass_count: 0, fail_count: 0, subtitle: '' },
          regulatory_hold: { count: 0, subtitle: '' }
        },
        tasks: { total_tasks: 0, critical_count: 0, tasks: [] },
        inspection_queue: [],
        qc_hold_items: [],
        quarantine_items: [],
        recent_activity: [],
        performance: {},
        compliance: { clauses: [], controls_active: 0 },
        generated_at: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // WebSocket for real-time updates
  const { isConnected, eventCount } = useInventoryWebSocket({
    enabled: true,
    onRefresh: () => loadDashboard(true),
    showToasts: true
  });

  const handleRefresh = async () => {
    await loadDashboard();
    toast.success('Dashboard refreshed');
  };

  const handleInspect = (inspection) => {
    navigate(`/inventory/receiving?tab=qc&inspection=${inspection.inspection_id}`);
  };

  const handleDisposition = (item) => {
    navigate(`/inventory/quarantine?item=${item.quarantine_id}`);
  };

  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#101957] mx-auto mb-4" />
          <p className="text-gray-500">Loading QC Inspector dashboard...</p>
        </div>
      </div>
    );
  }

  const summary = dashboardData?.summary_cards || {};
  const tasks = dashboardData?.tasks || { tasks: [], total_tasks: 0 };
  const inspectionQueue = dashboardData?.inspection_queue || [];
  const quarantineItems = dashboardData?.quarantine_items || [];
  const qcHoldItems = dashboardData?.qc_hold_items || [];
  const recentActivity = dashboardData?.recent_activity || [];
  const performance = dashboardData?.performance || {};
  const compliance = dashboardData?.compliance || {};

  return (
    <InventoryLayout>
      <div className="h-full overflow-auto" data-testid="qc-inspector-dashboard">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                <ClipboardCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-[#101957]">QC Inspector Dashboard</h1>
                  {/* Dashboard Selector for multi-role users */}
                  <DashboardSelector 
                    onSelect={handleDashboardSwitch}
                    currentPath={location.pathname}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {getGreeting()}. You have {summary.pending_inspections?.count || 0} inspections pending.
                </p>
              </div>
            </div>
          
          <div className="flex items-center gap-3">
            {/* AS9100D Badge */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    8.4.2 / 8.6 / 8.7
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs font-semibold">AS9100D Clauses Active</p>
                  <ul className="text-xs mt-1">
                    <li>8.4.2 - External Provider Control</li>
                    <li>8.6 - Release of Products</li>
                    <li>8.7 - Nonconforming Outputs</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* WebSocket Status */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge 
                    variant="outline" 
                    className={`gap-1 ${isConnected 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                    data-testid="websocket-status"
                  >
                    {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {isConnected ? 'Live' : 'Offline'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {isConnected 
                    ? `Real-time updates active (${eventCount} events)` 
                    : 'Real-time updates disconnected'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {lastRefresh && (
              <span className="text-xs text-gray-400">
                Updated {formatIST(lastRefresh)}
              </span>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards - 6 cards optimized for QC Inspector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <QCSummaryCard
            title="Pending Inspections"
            count={summary.pending_inspections?.count || 0}
            subtitle={summary.pending_inspections?.subtitle}
            icon={ClipboardCheck}
            color={(summary.pending_inspections?.overdue || 0) > 0 ? 'red' : 'blue'}
            urgent={(summary.pending_inspections?.overdue || 0) > 0}
            badge={summary.pending_inspections?.critical > 0 ? `${summary.pending_inspections.critical} Critical` : null}
            onClick={() => navigate('/inventory/receiving?tab=qc')}
            testId="pending-inspections-card"
          />
          <QCSummaryCard
            title="QC Hold Items"
            count={summary.qc_hold_items?.count || 0}
            subtitle={summary.qc_hold_items?.subtitle}
            icon={Clock}
            color={(summary.qc_hold_items?.urgent || 0) > 0 ? 'amber' : 'slate'}
            urgent={(summary.qc_hold_items?.urgent || 0) > 0}
            onClick={() => navigate('/inventory/lots?status=QC_HOLD')}
            testId="qc-hold-card"
          />
          <QCSummaryCard
            title="Quarantine Pending"
            count={summary.quarantine_pending?.count || 0}
            subtitle={summary.quarantine_pending?.subtitle}
            icon={AlertTriangle}
            color={(summary.quarantine_pending?.counterfeit_suspect || 0) > 0 ? 'red' : 'amber'}
            urgent={(summary.quarantine_pending?.counterfeit_suspect || 0) > 0}
            onClick={() => navigate('/inventory/quarantine')}
            testId="quarantine-pending-card"
          />
          <QCSummaryCard
            title="Completed Today"
            count={summary.completed_today?.count || 0}
            subtitle={summary.completed_today?.subtitle}
            icon={CheckCircle2}
            color="green"
            onClick={() => {}}
            testId="completed-today-card"
          />
          <QCSummaryCard
            title="Pass Rate (MTD)"
            count={summary.pass_rate?.count || 0}
            subtitle={summary.pass_rate?.subtitle}
            icon={TrendingUp}
            color={(summary.pass_rate?.count || 0) >= 90 ? 'emerald' : (summary.pass_rate?.count || 0) >= 70 ? 'amber' : 'red'}
            onClick={() => {}}
            testId="pass-rate-card"
          />
          <QCSummaryCard
            title="Regulatory Hold"
            count={summary.regulatory_hold?.count || 0}
            subtitle={summary.regulatory_hold?.subtitle}
            icon={Ban}
            color={(summary.regulatory_hold?.count || 0) > 0 ? 'red' : 'slate'}
            urgent={(summary.regulatory_hold?.count || 0) > 0}
            onClick={() => navigate('/inventory/quarantine?filter=regulatory')}
            testId="regulatory-hold-card"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Inspection Queue */}
          <div className="lg:col-span-2 space-y-6">
            {/* Inspection Queue */}
            <Card className="p-5" data-testid="inspection-queue-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-[#101957]" />
                  <h2 className="text-lg font-semibold text-[#101957]">Inspection Queue</h2>
                  {(summary.pending_inspections?.critical || 0) > 0 && (
                    <Badge variant="destructive">
                      {summary.pending_inspections.critical} Critical
                    </Badge>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/inventory/receiving?tab=qc')}
                  className="text-xs"
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {inspectionQueue.length > 0 ? (
                  inspectionQueue.slice(0, 6).map((inspection) => (
                    <InspectionQueueItem 
                      key={inspection.inspection_id} 
                      inspection={inspection}
                      onInspect={handleInspect}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
                    <p>No pending inspections. Great work!</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Quarantine Items */}
            <Card className="p-5" data-testid="quarantine-items-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-[#101957]">Quarantine - Pending Disposition</h2>
                  {(summary.quarantine_pending?.counterfeit_suspect || 0) > 0 && (
                    <Badge variant="destructive">
                      {summary.quarantine_pending.counterfeit_suspect} Counterfeit Suspect
                    </Badge>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/inventory/quarantine')}
                  className="text-xs"
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {quarantineItems.length > 0 ? (
                  quarantineItems.slice(0, 4).map((item) => (
                    <QuarantineItem 
                      key={item.quarantine_id} 
                      item={item}
                      onDisposition={handleDisposition}
                    />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p>No items pending disposition</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-5">
              <h2 className="text-lg font-semibold text-[#101957] mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/inventory/receiving?tab=qc')}
                >
                  <ClipboardCheck className="w-6 h-6 text-blue-600" />
                  <span className="text-sm">Inspection Queue</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/inventory/quarantine')}
                >
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  <span className="text-sm">Quarantine</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/inventory/lots?status=QC_HOLD')}
                >
                  <Package className="w-6 h-6 text-purple-600" />
                  <span className="text-sm">QC Hold Lots</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/inventory/receiving')}
                >
                  <FileCheck className="w-6 h-6 text-green-600" />
                  <span className="text-sm">View GRNs</span>
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Column - Activity & Metrics */}
          <div className="space-y-6">
            {/* Performance Metrics */}
            <Card className="p-5" data-testid="performance-metrics-card">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-[#101957]" />
                <h2 className="text-lg font-semibold text-[#101957]">Performance (MTD)</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <PerformanceMetric 
                  label="Inspected"
                  value={performance.completed_month || 0}
                  subtext="This month"
                />
                <PerformanceMetric 
                  label="Pass Rate"
                  value={`${performance.pass_rate || 0}%`}
                  subtext={`${performance.pass_count || 0} passed`}
                />
              </div>
              
              {/* Pass/Fail/Partial breakdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Passed
                  </span>
                  <span className="font-medium">{performance.pass_count || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    Failed
                  </span>
                  <span className="font-medium">{performance.fail_count || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Partial
                  </span>
                  <span className="font-medium">{performance.partial_count || 0}</span>
                </div>
              </div>
              
              {/* Progress bar visualization */}
              {(performance.completed_month || 0) > 0 && (
                <div className="mt-4">
                  <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                    <div 
                      className="bg-green-500" 
                      style={{ width: `${((performance.pass_count || 0) / performance.completed_month) * 100}%` }}
                    />
                    <div 
                      className="bg-red-500" 
                      style={{ width: `${((performance.fail_count || 0) / performance.completed_month) * 100}%` }}
                    />
                    <div 
                      className="bg-amber-500" 
                      style={{ width: `${((performance.partial_count || 0) / performance.completed_month) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Pass</span>
                    <span>Fail</span>
                    <span>Partial</span>
                  </div>
                </div>
              )}
            </Card>

            {/* Recent Activity */}
            <Card className="p-5" data-testid="recent-activity-card">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-[#101957]" />
                <h2 className="text-lg font-semibold text-[#101957]">Recent Activity</h2>
              </div>
              
              <div className="space-y-1">
                {recentActivity.length > 0 ? (
                  recentActivity.slice(0, 6).map((activity, idx) => (
                    <RecentActivityItem key={activity.inspection_id || idx} activity={activity} />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </Card>

            {/* AS9100D Compliance Card */}
            <Card className="p-5 bg-gradient-to-br from-[#101957] to-indigo-800 text-white">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                AS9100D Compliance
              </h3>
              <div className="space-y-2 text-sm">
                <p className="text-white/80">QC Inspector responsibilities per:</p>
                <ul className="space-y-2 text-white/90 text-xs">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span><strong>8.4.2</strong> - Verification of externally provided products</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span><strong>8.6</strong> - Release authority for products and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span><strong>8.7</strong> - Control of nonconforming outputs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span><strong>9.1</strong> - Monitoring and measurement</span>
                  </li>
                </ul>
                
                {compliance.items_requiring_attention > 0 && (
                  <div className="mt-3 p-2 bg-white/10 rounded-lg">
                    <p className="flex items-center gap-2 text-amber-300">
                      <AlertTriangle className="w-4 h-4" />
                      {compliance.items_requiring_attention} items require attention
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Part Configuration Changes Panel (AS9100D 8.1.2) */}
            <PartChangeNotificationPanel 
              showComplianceInfo={true}
              defaultExpanded={false}
            />

            {/* Last Updated */}
            {dashboardData?.generated_at && (
              <p className="text-xs text-center text-gray-400">
                Data as of: {formatIST(dashboardData.generated_at)}
              </p>
            )}
          </div>
        </div>
        </div>
      </div>
    </InventoryLayout>
  );
};

export default QCInspectorDashboard;
